/**
 * State preview helpers for the Properties states editor. Previews are editor
 * picks: the custom part goes through the engine's `previewState`, so a state
 * with no transition snaps as it does in Play, and a commit keeps the preview
 * showing.
 */

import { Properties } from 'trace-viewer/behaviors';
import { previewState } from 'trace-viewer/editor';
import { stateHome } from './state-routing';

/** Parse a compound state name into custom and pseudo parts ("state1:hover"). */
export function parseStateName(name: string): { custom?: string; pseudo?: string } {
    const colonIdx = name.indexOf(':');
    if (colonIdx === 0) return { pseudo: name };              // ":hover"
    if (colonIdx > 0) return {                                 // "state1:hover"
        custom: name.substring(0, colonIdx),
        pseudo: name.substring(colonIdx),
    };
    return { custom: name };                                   // "state1"
}

/**
 * Show a preview: the custom part across the group that owns it, and a pseudo
 * part (`:hover`, `gold:hover`) as this object's own pick, over its custom
 * state. Returns what ends it: each object it can touch gets its prior pick
 * back, so the state the strip shows returns; a prior pick whose state is gone
 * hands back to the runtime slot.
 */
export function applyPreview(behavior: Properties, name: string): () => void {
    const parts = parseStateName(name);
    const obj = behavior.sceneObject;
    const home = parts.custom ? stateHome(obj, parts.custom) : obj;
    const prior = new Map<Properties, string | null | undefined>();
    for (const o of new Set([home, ...home.getDescendants(), obj])) {
        const p = o.getBehavior(Properties);
        if (p) prior.set(p, p.getEditorState());
    }
    if (parts.custom) previewState(home, parts.custom);
    if (parts.pseudo) behavior.setEditorState(name, false);
    return () => {
        for (const [p, pick] of prior) p.setEditorState(pick, false);
    };
}
